/**
 * Calendar export (18 Oct 2026, web first — Chad's sequence step 3). ONE
 * direction only: ZoneIn writes an event the user adds to their own
 * calendar; it never reads a calendar (privacy positioning, battlecard).
 *
 * Two outputs, both built on the device from the task alone:
 *   - an .ics file (RFC 5545) — Apple Calendar, Outlook, and most others;
 *   - a Google Calendar "add event" link (format per the open reference
 *     InteractionDesignFoundation/add-event-to-calendar-docs — INFERRED
 *     from that secondary source, Google publishes no spec; the .ics path
 *     is the fallback that does not depend on it).
 * A date-only task (the app's noon convention) becomes an all-day event;
 * a task with a time becomes a timed event whose length is its open steps'
 * minutes (30 when it has no plan). A repeating task carries its rule.
 */
import { ZONE_BY_ID, type Task } from '../types';

const pad = (n: number) => String(n).padStart(2, '0');

function isAllDay(at: number): boolean {
  const d = new Date(at);
  return d.getHours() === 12 && d.getMinutes() === 0;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/** Floating local time (no Z): the event lands at that clock time wherever the calendar is. */
function ymdhms(d: Date): string {
  return `${ymd(d)}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
}

function utcStamp(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

export function eventMinutes(task: Task): number {
  const open = task.steps.filter((s) => !s.done).reduce((n, s) => n + (Number.isFinite(s.minutes) ? s.minutes : 0), 0);
  return Math.min(Math.max(open || 30, 15), 8 * 60);
}

/** RFC 5545 RRULE for a repeating task, or undefined. */
export function rruleFor(task: Task): string | undefined {
  switch (task.repeat) {
    case 'day':
      return 'FREQ=DAILY';
    case 'weekday':
      return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
    case 'week':
      return 'FREQ=WEEKLY';
    case 'month':
      return 'FREQ=MONTHLY';
    default:
      return undefined;
  }
}

/**
 * The user's choice for the EVENT only (18 Oct 2026, Chad: "time options
 * instead of an all-day event") — it never changes the task's own date.
 * `at`: 'allDay' or "HH:MM" on the task's day; `minutes`: event length.
 * Omitted fields fall back to the task: its own time (all-day for the
 * noon convention) and its plan's length.
 */
export interface EventChoice {
  at?: 'allDay' | string;
  minutes?: number;
}

/** "HH:MM" of a timed task, or 'allDay' for the noon (date-only) convention. */
export function defaultEventAt(task: Task): 'allDay' | string {
  if (task.deadlineAt === undefined || isAllDay(task.deadlineAt)) return 'allDay';
  const d = new Date(task.deadlineAt);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Length chips: 30 min, 1 hr, 2 hr — plus the plan's own length when different. */
export function lengthOptions(task: Task): number[] {
  const set = new Set([30, 60, 120]);
  if (task.steps.some((st) => !st.done)) set.add(eventMinutes(task));
  return [...set].sort((a, b) => a - b);
}

/** Start/end as all-day dates or floating local times. */
export function eventTimes(task: Task, choice: EventChoice = {}): { allDay: boolean; start: string; end: string } | undefined {
  if (task.deadlineAt === undefined || !Number.isFinite(task.deadlineAt)) return undefined;
  const day = new Date(task.deadlineAt);
  const at = choice.at ?? defaultEventAt(task);
  const m = typeof at === 'string' ? /^(\d{1,2}):(\d{2})$/.exec(at) : null;
  if (at === 'allDay' || !m || Number(m[1]) > 23 || Number(m[2]) > 59) {
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
    return { allDay: true, start: ymd(day), end: ymd(next) };
  }
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), Number(m[1]), Number(m[2]));
  const minutes = Math.min(Math.max(choice.minutes ?? eventMinutes(task), 15), 8 * 60);
  const end = new Date(start.getTime() + minutes * 60000);
  return { allDay: false, start: ymdhms(start), end: ymdhms(end) };
}

export function eventDetails(task: Task): string {
  const lines = [`ZoneIn AI — ${ZONE_BY_ID[task.zone]?.title ?? ''}`.trim()];
  const open = task.steps.filter((s) => !s.done);
  if (open.length) {
    lines.push('', 'Plan:');
    open.forEach((s, i) => lines.push(`${i + 1}. ${s.text} (${s.minutes} min)`));
  }
  return lines.join('\n');
}

/** RFC 5545 text escaping. */
function esc(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Fold content lines at 74 characters (RFC 5545 §3.1: continuation starts with a space). */
function fold(line: string): string {
  if (line.length <= 74) return line;
  const parts: string[] = [];
  for (let i = 0; i < line.length; i += 73) parts.push((i === 0 ? '' : ' ') + line.slice(i, i + 73));
  return parts.join('\r\n');
}

export function icsFor(task: Task, now: number = Date.now(), choice: EventChoice = {}): string | undefined {
  const t = eventTimes(task, choice);
  if (!t) return undefined;
  const rrule = rruleFor(task);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ZoneIn AI//Task export//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${task.id.replace(/[^\w~-]/g, '')}@zoneinai.com`,
    `DTSTAMP:${utcStamp(now)}`,
    t.allDay ? `DTSTART;VALUE=DATE:${t.start}` : `DTSTART:${t.start}`,
    t.allDay ? `DTEND;VALUE=DATE:${t.end}` : `DTEND:${t.end}`,
    ...(rrule ? [`RRULE:${rrule}`] : []),
    `SUMMARY:${esc(task.text)}`,
    `DESCRIPTION:${esc(eventDetails(task))}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return `${lines.map(fold).join('\r\n')}\r\n`;
}

export function googleCalendarUrl(task: Task, choice: EventChoice = {}): string | undefined {
  const t = eventTimes(task, choice);
  if (!t) return undefined;
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: task.text,
    dates: `${t.start}/${t.end}`,
    details: eventDetails(task),
  });
  const rrule = rruleFor(task);
  if (rrule) q.set('recur', `RRULE:${rrule}`);
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

/** A filename that is safe on every OS. */
export function icsFileName(task: Task): string {
  const base = task.text.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'task';
  return `${base}.ics`;
}
